import React, { useState, useEffect } from 'react';
import { 
  Search, 
  History,
  User,
  Building2,
  Users,
  Receipt,
  FileText,
  Plus,
  Pencil,
  Trash2,
  LogIn,
  Eye,
  Filter,
  Clock,
  Download,
  FileSpreadsheet
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { activityLogsAPI, collaboratorsAPI } from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const actionLabels = {
  create: 'Création',
  update: 'Modification',
  delete: 'Suppression',
  login: 'Connexion',
  view: 'Consultation'
};

const actionIcons = {
  create: Plus,
  update: Pencil,
  delete: Trash2,
  login: LogIn,
  view: Eye
};

const actionColors = {
  create: 'bg-green-100 text-green-800',
  update: 'bg-blue-100 text-blue-800',
  delete: 'bg-red-100 text-red-800',
  login: 'bg-purple-100 text-purple-800',
  view: 'bg-gray-100 text-gray-800'
};

const resourceLabels = {
  property: 'Bien',
  client: 'Client',
  transaction: 'Transaction',
  rent_payment: 'Paiement loyer',
  user: 'Utilisateur'
};

const resourceIcons = {
  property: Building2,
  client: Users,
  transaction: Receipt,
  rent_payment: FileText,
  user: User
};

const ActivityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAction, setFilterAction] = useState('all');
  const [filterResource, setFilterResource] = useState('all');
  const [filterEmployee, setFilterEmployee] = useState('all');
  const [activeTab, setActiveTab] = useState('logs');
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [logsRes, collabRes] = await Promise.all([
        activityLogsAPI.getAll({ limit: 100 }),
        collaboratorsAPI.getAll()
      ]);
      setLogs(logsRes.data);
      setCollaborators(collabRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des donnees');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filterAction !== 'all') params.append('action', filterAction);
      if (filterResource !== 'all') params.append('resource_type', filterResource);
      if (filterEmployee !== 'all') params.append('employee_id', filterEmployee);
      
      const response = await fetch(
        `${BACKEND_URL}/api/activity-logs/export/${format}?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (!response.ok) throw new Error('Erreur export');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `historique_activites_${new Date().toISOString().slice(0,10)}.${format === 'excel' ? 'csv' : 'pdf'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Export ${format.toUpperCase()} telecharge avec succes`);
    } catch (error) {
      toast.error(`Erreur lors de l'export ${format.toUpperCase()}`);
    } finally {
      setExporting(null);
    }
  };

  const filteredLogs = logs.filter(log => {
    const matchesSearch = 
      (log.user_name && log.user_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (log.employee_id && log.employee_id.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (log.resource_title && log.resource_title.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (log.details && log.details.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesAction = filterAction === 'all' || log.action === filterAction;
    const matchesResource = filterResource === 'all' || log.resource_type === filterResource;
    const matchesEmployee = filterEmployee === 'all' || log.employee_id === filterEmployee;
    
    return matchesSearch && matchesAction && matchesResource && matchesEmployee;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="activity-logs-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
            Tracabilite & Collaborateurs
          </h1>
          <p className="text-gray-500 mt-1">
            Historique des actions et gestion des collaborateurs
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => handleExport('pdf')}
            disabled={exporting === 'pdf'}
            data-testid="export-pdf-btn"
          >
            {exporting === 'pdf' ? (
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Export PDF
          </Button>
          <Button
            variant="outline"
            onClick={() => handleExport('excel')}
            disabled={exporting === 'excel'}
            data-testid="export-excel-btn"
          >
            {exporting === 'excel' ? (
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
            ) : (
              <FileSpreadsheet className="w-4 h-4 mr-2" />
            )}
            Export Excel
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('logs')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'logs' 
              ? 'border-[#1B3A4B] text-[#1B3A4B]' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          data-testid="tab-logs"
        >
          <History className="w-4 h-4 inline-block mr-2" />
          Historique des actions
        </button>
        <button
          onClick={() => setActiveTab('collaborators')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'collaborators' 
              ? 'border-[#1B3A4B] text-[#1B3A4B]' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          data-testid="tab-collaborators"
        >
          <Users className="w-4 h-4 inline-block mr-2" />
          Collaborateurs ({collaborators.length})
        </button>
      </div>

      {activeTab === 'logs' && (
        <>
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Rechercher dans l'historique..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="search-logs-input"
                  />
                </div>
                <Select value={filterAction} onValueChange={setFilterAction}>
                  <SelectTrigger className="w-full lg:w-[160px]" data-testid="filter-action-select">
                    <SelectValue placeholder="Action" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes actions</SelectItem>
                    <SelectItem value="create">Création</SelectItem>
                    <SelectItem value="update">Modification</SelectItem>
                    <SelectItem value="delete">Suppression</SelectItem>
                    <SelectItem value="login">Connexion</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterResource} onValueChange={setFilterResource}>
                  <SelectTrigger className="w-full lg:w-[160px]" data-testid="filter-resource-select">
                    <SelectValue placeholder="Ressource" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes ressources</SelectItem>
                    <SelectItem value="property">Biens</SelectItem>
                    <SelectItem value="client">Clients</SelectItem>
                    <SelectItem value="transaction">Transactions</SelectItem>
                    <SelectItem value="rent_payment">Paiements</SelectItem>
                    <SelectItem value="user">Utilisateurs</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterEmployee} onValueChange={setFilterEmployee}>
                  <SelectTrigger className="w-full lg:w-[200px]" data-testid="filter-employee-select">
                    <SelectValue placeholder="Collaborateur" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les collaborateurs</SelectItem>
                    {collaborators.map((collab) => (
                      <SelectItem key={collab.employee_id} value={collab.employee_id}>
                        {collab.employee_id} - {collab.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Activity Table */}
          {filteredLogs.length > 0 ? (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date & Heure</TableHead>
                      <TableHead>Collaborateur</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Ressource</TableHead>
                      <TableHead>Détails</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLogs.map((log) => {
                      const ActionIcon = actionIcons[log.action] || Eye;
                      const ResourceIcon = resourceIcons[log.resource_type] || FileText;
                      
                      return (
                        <TableRow key={log.id}>
                          <TableCell className="whitespace-nowrap">
                            <div className="flex items-center gap-2 text-sm">
                              <Clock className="w-3.5 h-3.5 text-gray-400" />
                              {formatDateTime(log.timestamp)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-[#D4A373] flex items-center justify-center flex-shrink-0">
                                <span className="text-white text-xs font-medium">
                                  {log.user_name?.charAt(0).toUpperCase() || 'U'}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium text-gray-900 text-sm">{log.user_name}</p>
                                <p className="text-xs font-mono text-[#1B3A4B]">{log.employee_id}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={`${actionColors[log.action]} flex items-center gap-1 w-fit`}>
                              <ActionIcon className="w-3 h-3" />
                              {actionLabels[log.action]}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <ResourceIcon className="w-4 h-4 text-gray-400" />
                              <div>
                                <p className="text-sm">{resourceLabels[log.resource_type]}</p>
                                {log.resource_title && (
                                  <p className="text-xs text-gray-500 truncate max-w-[150px]">
                                    {log.resource_title}
                                  </p>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm text-gray-600 truncate max-w-[250px]">
                              {log.details}
                            </p>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12">
                <div className="text-center">
                  <History className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune activité trouvée</h3>
                  <p className="text-gray-500">
                    {searchQuery || filterAction !== 'all' || filterResource !== 'all' || filterEmployee !== 'all'
                      ? 'Essayez de modifier vos filtres'
                      : 'L\'historique des actions apparaîtra ici'}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {activeTab === 'collaborators' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Liste des Collaborateurs</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {collaborators.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Collaborateur</TableHead>
                    <TableHead>Identifiant</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Rôle</TableHead>
                    <TableHead>Actions effectuées</TableHead>
                    <TableHead>Dernière activité</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {collaborators.map((collab) => (
                    <TableRow key={collab.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#1B3A4B] flex items-center justify-center">
                            <span className="text-white font-medium">
                              {collab.name?.charAt(0).toUpperCase() || 'U'}
                            </span>
                          </div>
                          <span className="font-medium">{collab.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded text-[#1B3A4B] font-semibold">
                          {collab.employee_id}
                        </span>
                      </TableCell>
                      <TableCell className="text-gray-600">{collab.email}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {collab.role === 'admin' ? 'Administrateur' : 'Agent'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="font-semibold text-[#1B3A4B]">{collab.activity_count}</span>
                      </TableCell>
                      <TableCell className="text-gray-500 text-sm">
                        {collab.last_activity ? (
                          <div>
                            <p>{formatDateTime(collab.last_activity.timestamp)}</p>
                            <p className="text-xs text-gray-400">
                              {actionLabels[collab.last_activity.action]} - {resourceLabels[collab.last_activity.resource_type]}
                            </p>
                          </div>
                        ) : (
                          'Aucune activité'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">Aucun collaborateur enregistré</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ActivityLogs;
